import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/system-users/SystemUsersAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { SystemUsersFilter } from "./system-users-filter/SystemUsersFilter";
import { SystemUsersTable } from "./system-users-table/SystemUsersTable";
import { useSystemUsersUIContext } from "./SystemUsersUIContext";

export function SystemUsersCard() {
  // SystemUsers UI Context
  const systemUsersUIContext = useSystemUsersUIContext();
  const systemUsersUIProps = useMemo(() => {
    return {
      ids: systemUsersUIContext.ids,
      setIds: systemUsersUIContext.setIds,
      queryParams: systemUsersUIContext.queryParams,
      openNewSystemUserPage: systemUsersUIContext.openNewSystemUserPage,
    };
  }, [systemUsersUIContext]);

  // Getting curret state of SystemUsers list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.systemUsers }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // SystemUsers Redux state
  const dispatch = useDispatch();  

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Kullanıcı Listesini Excele Aktarır */
  const exportSystemUsersToExcel = () => {
    dispatch(actions.getAllSystemUsers(systemUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 25, 15, 15];
      const columns = ["Adı", "Soyadı", "Kullanıcı Adı", "Süper Admin", "Aktif mi?"];
      entities.forEach(entity => rows.push([entity.name, entity.surname, entity.username, entity.isSuperAdmin ? "Evet" : "Hayır", entity.isActive ? "Aktif" : "Pasif"]));
      const filename = "Kullanıcı Listesi"
      exportToExcel({ columns, rows, widths, filename });
    });
  }

  /** Belli Bir Kullanıcı Listesini PDF e Aktarır */
  const exportSystemUsersToPDF = () => {
    dispatch(actions.getAllSystemUsers(systemUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Kullanıcı Adı",
          rows: entities.map(entity => entity.username)
        },
        {
          header: "Süper Admin",
          rows: entities.map(entity => entity.isSuperAdmin ? "Evet" : "Hayır")
        },
        {
          header: "Aktif mi?",
          rows: entities.map(entity => entity.isActive ? "Aktif" : "Pasif")
        }
      ];
  
      const filename = "Kullanıcı Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Kullanıcı Listesini Yazdırma Penceresini Açar */
  const printSystemUsers = () => {
    dispatch(actions.getAllSystemUsers(systemUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;      
      const data = [];    
      const header = "Kullanıcı Listesi";
      const properties = ["Adı", "Soyadı", "Kullanıcı Adı", "Süper Admin", "Aktif mi?"];

      entities.forEach(entity => {
        data.push({
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Kullanıcı Adı": entity.username,
          "Süper Admin": entity.isSuperAdmin ? "Evet" : "Hayır",
          "Aktif mi?": entity.isActive ? "Aktif" : "Pasif"
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Kullanıcı Listesini Panoya Kopyalar */
  const copySystemUsers = () => {
    dispatch(actions.getAllSystemUsers(systemUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Kullanıcı Listesi";
      const columns = ["Adı", "Soyadı", "Kullanıcı Adı", "Süper Admin", "Aktif mi?"];
      entities.forEach(entity => rows.push([entity.name, entity.surname, entity.username, entity.isSuperAdmin ? "Evet" : "Hayır", entity.isActive ? "Aktif" : "Pasif"]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesi Siliniyor */
  const deleteSystemUsers = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }
  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteSystemUsers(ids)).then(result => onresult(result));
    });
  }

  const onresult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        systemUsersUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getSystemUsers(systemUsersUIProps.queryParams));        
      }, 2500);
    }
  };
  
  const title = <>Kullanıcı Listesi{systemUsersUIProps.ids.length > 0 ? 
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{systemUsersUIProps.ids.length}</span>]</> : 
                  ""}
                </>;
  
  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportSystemUsersToExcel}
                exportToPDF={exportSystemUsersToPDF}
                print={printSystemUsers}
                copy={copySystemUsers}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Kullanıcı"
                method={systemUsersUIProps.openNewSystemUserPage}
              />
            }              
            {
              isDeletionAuthorized &&
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteSystemUsers(systemUsersUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <SystemUsersFilter />
        <SystemUsersTable deleteSystemUsers={deleteSystemUsers}/>
      </CardBody>
    </Card>
  );
}